import React, { useState } from "react";
import { Box, Typography, Button, useTheme } from "@mui/material";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import cardData from "./data/articles";

export default function WebinarPodcastCards() {
  const theme = useTheme();

  const [expanded, setExpanded] = useState({});
  const [playingVideoId, setPlayingVideoId] = useState(null);

  const categories = ["WEBINARS", "PODCASTS"];

  const toggleExpand = (category) => {
    setExpanded((prev) => ({ ...prev, [category]: !prev[category] }));
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 6, p: 4 }}>
      {categories.map((category) => {
        const cards = cardData.filter((c) => c.category === category);
        if (cards.length === 0) return null;

        const isExpanded = expanded[category];
        const visibleCards = isExpanded ? cards : cards.slice(0, 2);

        return (
          <Box key={category}>
            <Typography
              variant="h4"
              sx={{
                mb: 4,
                fontWeight: "bold",
                textAlign: "center",
                color: theme.palette.text.primary,
              }}
            >
              {category}
            </Typography>

            {visibleCards.map((card, index) => {
              const isEven = index % 2 === 0;
              const isPlaying = playingVideoId === card.id;

              return (
                <motion.div
                  key={card.id}
                  initial={{ opacity: 0, x: isEven ? 100 : -100 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: false, amount: 0.3 }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: {
                        xs: 'column',
                        sm: 'column',
                        md: card.img ? (isEven ? 'row' : 'row-reverse') : 'column',
                      },
                      alignItems: 'center',
                      gap: 3,
                      borderRadius: 3,
                      p: 3,
                      mb: 2,
                      boxShadow: '0px 4px 10px rgba(0,0,0,0.1)',
                      minHeight: 200,
                    }}
                  >
                    {/* ✅ Image or Video */}
                    {card.video ? (
                      !isPlaying ? (
                        <Box
                          component="img"
                          src={card.img}
                          alt={card.title}
                          onClick={() => setPlayingVideoId(card.id)}
                          sx={{
                            width: { xs: '100%', sm: '100%', md: 400 },
                            height: { xs: 180, sm: 200, md: 200 },
                            borderRadius: 2,
                            objectFit: 'cover',
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <Box
                          component="video"
                          src={card.video}
                          controls
                          autoPlay
                          sx={{
                            width: { xs: '100%', sm: '100%', md: 400 },
                            height: { xs: 180, sm: 200, md: 200 },
                            borderRadius: 2,
                            objectFit: 'cover',
                            flexShrink: 0,
                          }}
                        />
                      )
                    ) : (
                      card.img && (
                        <Box
                          component="img"
                          src={card.img}
                          alt={card.title}
                          sx={{
                            width: { xs: '100%', sm: '100%', md: 400 },
                            height: { xs: 180, sm: 200, md: 200 },
                            borderRadius: 2,
                            objectFit: 'cover',
                            flexShrink: 0,
                          }}
                        />
                      )
                    )}

                    {/* Text */}
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        component={Link}
                        to={`/article/${card.id}`}
                        sx={{
                          display: "block",
                          fontSize: { xs: "1.05rem", md: "1.25rem" },
                          fontWeight: "bold",
                          color: card.bgColor || "#1976d2",
                          mb: 1,
                          textDecoration: "none",
                          "&:hover": {
                            textDecoration: "underline",
                            textDecorationColor: card.bgColor || "#1976d2",
                          },
                        }}
                      >
                        {card.title}
                      </Typography>

                      <Typography variant="body1" color={theme.palette.text.primary} >
                        {Array.isArray(card.text)
                          ? card.text.join(" ")
                          : card.text}
                      </Typography>
                    </Box>
                  </Box>
                </motion.div>
              );
            })}

            {/* See More Button */}
            {cards.length > 2 && (
              <Button
                variant="contained"
                onClick={() => toggleExpand(category)}
                sx={{
                  mt: 1,
                  backgroundColor: "#00AEEF",
                  color: "white",
                  px: 4,
                  py: 1.2,
                  borderRadius: "20px",
                  textTransform: "none",
                  fontWeight: 500,
                  "&:hover": { backgroundColor: "#008ECC" },
                  display: "flex",
                  marginLeft: "auto",
                }}
              >
                {isExpanded ? "See Less" : "See More"}
              </Button>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
